import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import {
  LogOut, Calculator, Users, Building2, Database,
  Plus, TrendingUp, Loader2, Home, Rocket, Zap,
  Menu, X, ChevronRight, ArrowUpRight,
} from 'lucide-react';
import { Link, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { BankManagement } from './banks/BankManagement';
import { BankDetails } from './banks/BankDetails';
import { BankForm } from './banks/BankForm';
import { MerchantManagement } from './merchants/MerchantManagement';
import { MerchantDetails } from './merchants/MerchantDetails';
import { MerchantForm } from './merchants/MerchantForm';
import { QuotationList } from './quotations/QuotationList';
import { NewQuotation } from './quotations/NewQuotation';
import { QuoteComparison } from './quotations/QuoteComparison';
import { QuotationDetails } from './quotations/QuotationDetails';
import { SavedDatasets } from './datasets/SavedDatasets';
import DatasetFeeReport from './datasets/DatasetFeeReport';
import { EditDatasetMapping } from './datasets/EditDatasetMapping';
import { NewDataset } from './datasets/NewDataset';
import { DatasetDetails } from './datasets/DatasetDetails';
import { TransactionList } from './datasets/TransactionList';
import { QuickQuote } from './quick-quote/QuickQuote';
import { SavedQuickQuotes } from './quick-quote/SavedQuickQuotes';
import type { User } from '@supabase/supabase-js';

const navigation = [
  { name: 'Home', href: '/', icon: Home },
  { name: 'Quotations', href: '/quotations', icon: Calculator, action: { name: 'New Quotation', href: '/quotations/new', icon: Plus } },
  { name: 'Quick Quote', href: '/quick-quote', icon: Zap },
  { name: 'Datasets', href: '/datasets', icon: Database, action: { name: 'New Dataset', href: '/datasets/new', icon: Plus } },
  { name: 'Providers', href: '/banks', icon: Building2, action: { name: 'Add Provider', href: '/banks/new', icon: Plus } },
  { name: 'Merchants', href: '/merchants', icon: Users, action: { name: 'Add Merchant', href: '/merchants/new', icon: Plus } },
];

function getUserInitials(user: User): string {
  const meta = user.user_metadata;
  if (meta?.full_name) {
    return meta.full_name.split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2);
  }
  if (meta?.name) {
    return meta.name.split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2);
  }
  return (user.email || '??')[0].toUpperCase();
}

function getUserDisplayName(user: User): string {
  const meta = user.user_metadata;
  return meta?.full_name || meta?.name || user.email?.split('@')[0] || 'User';
}

export function Dashboard({ user }: { user: User }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-5 flex-shrink-0">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-lg flex items-center justify-center">
            <Rocket className="w-4.5 h-4.5 text-white" />
          </div>
          <span className="text-[15px] font-semibold text-white tracking-tight">SpaceQuote</span>
        </Link>
        <button
          onClick={() => setSidebarOpen(false)}
          className="md:hidden p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
        {navigation.map((item) => {
          const active = isActive(item.href);
          return (
            <div key={item.name} className="group relative">
              <Link
                to={item.href}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] font-medium transition-all duration-150 ${
                  active
                    ? 'bg-white/[0.12] text-white'
                    : 'text-slate-400 hover:text-white hover:bg-white/[0.06]'
                }`}
              >
                <item.icon className={`w-[18px] h-[18px] flex-shrink-0 ${active ? 'text-violet-400' : ''}`} />
                {item.name}
                {active && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-violet-400 rounded-r-full" />}
              </Link>
              {item.action && (
                <button
                  onClick={() => { navigate(item.action!.href); setSidebarOpen(false); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-1.5 rounded-md text-slate-500 hover:text-white hover:bg-white/10 transition-all duration-150"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          );
        })}
      </nav>

      {/* User section */}
      <div className="flex-shrink-0 border-t border-white/[0.08] p-3">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0">
            {getUserInitials(user)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-medium text-slate-200 truncate">{getUserDisplayName(user)}</p>
            <p className="text-[11px] text-slate-500 truncate">{user.email}</p>
          </div>
        </div>
        <button
          onClick={handleSignOut}
          className="w-full flex items-center gap-2.5 px-3 py-2 mt-1 rounded-lg text-[13px] font-medium text-slate-500 hover:text-red-400 hover:bg-white/[0.06] transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col md:flex-row">
      {/* Mobile top bar */}
      <div className="md:hidden flex items-center justify-between bg-slate-900 px-4 h-14 flex-shrink-0 z-30">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-7 h-7 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-lg flex items-center justify-center">
            <Rocket className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="text-[15px] font-semibold text-white tracking-tight">SpaceQuote</span>
        </Link>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center text-[10px] font-bold text-white">
            {getUserInitials(user)}
          </div>
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile drawer */}
      <div
        className={`md:hidden fixed inset-y-0 left-0 z-50 w-[260px] bg-slate-900 flex flex-col transform transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {sidebarContent}
      </div>

      {/* Desktop sidebar */}
      <div className="hidden md:flex w-[240px] bg-slate-900 flex-col flex-shrink-0 fixed inset-y-0 left-0 z-20">
        {sidebarContent}
      </div>

      {/* Main Content */}
      <main className="flex-1 md:ml-[240px] min-w-0">
        <div className="max-w-6xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <Routes>
            <Route path="/banks" element={<BankManagement />} />
            <Route path="/banks/:bankId" element={<BankDetails />} />
            <Route path="/banks/new" element={<BankForm onClose={() => navigate('/banks')} onSuccess={() => navigate('/banks')} />} />
            <Route path="/merchants" element={<MerchantManagement />} />
            <Route path="/merchants/:merchantId" element={<MerchantDetails />} />
            <Route path="/merchants/new" element={<MerchantForm onClose={() => navigate('/merchants')} onSuccess={() => navigate('/merchants')} />} />
            <Route path="/quotations/new" element={<NewQuotation />} />
            <Route path="/quotations" element={<QuotationList />} />
            <Route path="/quotations/compare/:quoteIds" element={<QuoteComparison />} />
            <Route path="/quotations/:quotationId" element={<QuotationDetails />} />
            <Route path="/quick-quote" element={<QuickQuote />} />
            <Route path="/quick-quote/saved" element={<SavedQuickQuotes />} />
            <Route path="/datasets" element={<SavedDatasets />} />
            <Route path="/datasets/new" element={<NewDataset />} />
            <Route path="/datasets/:datasetId" element={<DatasetDetails />} />
            <Route path="/datasets/mappings/:mappingId/edit" element={<EditDatasetMapping />} />
            <Route path="/datasets/:datasetId/fees" element={<DatasetFeeReport />} />
            <Route path="/datasets/transactions" element={<TransactionList />} />
            <Route path="/" element={<DashboardHome user={user} />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

function DashboardHome({ user }: { user: User }) {
  const [stats, setStats] = useState({
    quotations: 0,
    merchants: 0,
    banks: 0,
    total_datasets: 0,
    total_volume: 0,
    total_site_fees: 0,
    total_bank_fees: 0,
    total_reseller_fees: 0,
    totalRevenue: 0,
    recentActivity: [] as {
      id: string;
      action: string;
      entity_type: string;
      entity_id: string;
      entity_name: string;
      created_at: string;
    }[]
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardStats();
  }, []);

  const loadDashboardStats = async () => {
    try {
      const [quotationsResult, merchantsResult, banksResult, datasetsResult, activityResult] = await Promise.all([
        supabase.from('quotations').select('*').eq('archived', false),
        supabase.from('merchants').select('*'),
        supabase.from('banks').select('*').eq('deleted', false),
        supabase.from('datasets').select('id, total_volume, total_site_fees, total_bank_fees, total_reseller_fees'),
        supabase.rpc('get_recent_activity', {
          user_id_param: (await supabase.auth.getUser()).data.user?.id,
          limit_param: 5
        })
      ]);

      if (quotationsResult.error) throw quotationsResult.error;
      if (merchantsResult.error) throw merchantsResult.error;
      if (banksResult.error) throw banksResult.error;
      if (datasetsResult.error) throw datasetsResult.error;
      if (activityResult.error) throw activityResult.error;

      const totalVolume = datasetsResult.data.reduce((sum, d) => sum + (d.total_volume || 0), 0);
      const totalSiteFees = datasetsResult.data.reduce((sum, d) => sum + (d.total_site_fees || 0), 0);
      const totalBankFees = datasetsResult.data.reduce((sum, d) => sum + (d.total_bank_fees || 0), 0);
      const totalResellerFees = datasetsResult.data.reduce((sum, d) => sum + (d.total_reseller_fees || 0), 0);

      setStats({
        quotations: quotationsResult.data?.length || 0,
        merchants: merchantsResult.data?.length || 0,
        banks: banksResult.data?.length || 0,
        total_datasets: datasetsResult.data?.length || 0,
        total_volume: totalVolume,
        total_site_fees: totalSiteFees,
        total_bank_fees: totalBankFees,
        total_reseller_fees: totalResellerFees,
        totalRevenue: totalSiteFees - totalBankFees - totalResellerFees,
        recentActivity: activityResult.data || []
      });
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const timeOfDay = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const statCards = [
    { label: 'Datasets', value: stats.total_datasets, icon: Database, color: 'emerald', href: '/datasets' },
    { label: 'Merchants', value: stats.merchants, icon: Users, color: 'blue', href: '/merchants' },
    { label: 'Providers', value: stats.banks, icon: Building2, color: 'violet', href: '/banks' },
    { label: 'Quotations', value: stats.quotations, icon: Calculator, color: 'amber', href: '/quotations' },
  ] as const;

  const colorMap = {
    emerald: { bg: 'bg-emerald-50', icon: 'text-emerald-600', link: 'text-emerald-600 hover:text-emerald-700' },
    blue: { bg: 'bg-blue-50', icon: 'text-blue-600', link: 'text-blue-600 hover:text-blue-700' },
    violet: { bg: 'bg-violet-50', icon: 'text-violet-600', link: 'text-violet-600 hover:text-violet-700' },
    amber: { bg: 'bg-amber-50', icon: 'text-amber-600', link: 'text-amber-600 hover:text-amber-700' },
  } as const;

  const quickActions = [
    { label: 'New Quotation', desc: 'Create a fee quotation', href: '/quotations/new', icon: Calculator, color: 'bg-violet-50 hover:bg-violet-100', iconColor: 'text-violet-600' },
    { label: 'Quick Quote', desc: 'Compare rates fast', href: '/quick-quote', icon: Zap, color: 'bg-amber-50 hover:bg-amber-100', iconColor: 'text-amber-600' },
    { label: 'New Dataset', desc: 'Import transactions', href: '/datasets/new', icon: Database, color: 'bg-emerald-50 hover:bg-emerald-100', iconColor: 'text-emerald-600' },
    { label: 'New Merchant', desc: 'Add a merchant', href: '/merchants/new', icon: Users, color: 'bg-blue-50 hover:bg-blue-100', iconColor: 'text-blue-600' },
  ];

  const entityIcons: Record<string, React.ComponentType<{ className?: string }>> = {
    quotation: Calculator,
    merchant: Users,
    bank: Building2,
    dataset: Database,
  };

  return (
    <div className="space-y-8">
      {/* Greeting */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
          {timeOfDay()}, {getUserDisplayName(user)}
        </h1>
        <p className="mt-1.5 text-slate-500 text-[15px]">
          Here's what's happening with your pricing workspace.
        </p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card) => {
          const colors = colorMap[card.color];
          return (
            <Link
              key={card.label}
              to={card.href}
              className="bg-white rounded-xl border border-slate-200/80 p-5 hover:border-slate-300 hover:shadow-sm transition-all duration-200 group"
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`w-9 h-9 ${colors.bg} rounded-lg flex items-center justify-center`}>
                  <card.icon className={`w-[18px] h-[18px] ${colors.icon}`} />
                </div>
                <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-slate-400 transition-colors" />
              </div>
              <p className="text-2xl font-bold text-slate-900">{loading ? '—' : card.value}</p>
              <p className="text-[13px] text-slate-500 mt-0.5">{card.label}</p>
            </Link>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Quick Actions */}
        <div className="lg:col-span-2">
          <h2 className="text-[15px] font-semibold text-slate-900 mb-3">Quick actions</h2>
          <div className="space-y-2">
            {quickActions.map((action) => (
              <Link
                key={action.label}
                to={action.href}
                className={`flex items-center gap-3.5 p-3.5 rounded-xl ${action.color} transition-colors duration-150 group`}
              >
                <div className="w-9 h-9 bg-white/80 rounded-lg flex items-center justify-center flex-shrink-0">
                  <action.icon className={`w-[18px] h-[18px] ${action.iconColor}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold text-slate-900">{action.label}</p>
                  <p className="text-[12px] text-slate-500">{action.desc}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-400 group-hover:translate-x-0.5 transition-all" />
              </Link>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="lg:col-span-3">
          <h2 className="text-[15px] font-semibold text-slate-900 mb-3">Recent activity</h2>
          <div className="bg-white rounded-xl border border-slate-200/80 divide-y divide-slate-100">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : !stats.recentActivity || stats.recentActivity.length === 0 ? (
              <div className="py-16 text-center">
                <div className="w-12 h-12 mx-auto mb-3 bg-slate-100 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-slate-400" />
                </div>
                <p className="text-[13px] text-slate-500">No recent activity yet</p>
                <p className="text-[12px] text-slate-400 mt-1">Actions you take will show up here</p>
              </div>
            ) : (
              stats.recentActivity.map((activity) => {
                const Icon = entityIcons[activity.entity_type] || Database;
                return (
                  <div key={activity.id} className="flex items-center gap-3.5 px-4 py-3.5">
                    <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-slate-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] text-slate-700">
                        <span className="capitalize">{activity.action}</span>{' '}
                        <Link
                          to={`/${activity.entity_type}s/${activity.entity_id}`}
                          className="font-medium text-violet-600 hover:text-violet-700"
                        >
                          {activity.entity_name}
                        </Link>
                      </p>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        {new Date(activity.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
